import type { LoginInput, RegisterInput, AuthTokens } from '@hr/shared';
import { API_BASE_URL, BFF_API_URL, USE_BFF } from './constants';

// Determine which API URL to use
const API_URL = USE_BFF ? BFF_API_URL : API_BASE_URL;

export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

// Token refresh promise to prevent multiple simultaneous refresh attempts
let refreshPromise: Promise<string | null> | null = null;

// Track if we're currently redirecting to prevent multiple redirects
let isRedirectingToLogin = false;

/**
 * Redirect to login page and clear auth state
 * Only redirects once to prevent loops
 */
function redirectToLogin(): void {
  if (isRedirectingToLogin) return;
  if (typeof window === 'undefined') return;

  isRedirectingToLogin = true;

  // Clear all auth tokens
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');

  // Redirect to login with return URL
  const currentPath = window.location.pathname + window.location.search;
  const returnUrl = currentPath !== '/login' ? `?returnUrl=${encodeURIComponent(currentPath)}` : '';
  window.location.href = `/login${returnUrl}`;
}

/**
 * Attempt to refresh the access token using the BFF refresh endpoint
 */
async function refreshAccessToken(): Promise<string | null> {
  // If already refreshing, wait for that to complete
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include', // Include cookies
      });

      if (!response.ok) {
        // Refresh failed - clear auth state and redirect to login
        if (typeof window !== 'undefined') {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
        // Redirect to login since session is invalid
        redirectToLogin();
        return null;
      }

      const data = await response.json();
      const newToken = data.accessToken;

      // Store new token
      if (typeof window !== 'undefined' && newToken) {
        localStorage.setItem('accessToken', newToken);
      }

      return newToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Redirect to login on refresh failure
      redirectToLogin();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {},
  retryCount: number = 0
): Promise<T> {
  // Determine the URL based on mode and endpoint
  let url: string;

  if (USE_BFF) {
    // In BFF mode, map endpoints appropriately
    if (endpoint.startsWith('/auth/')) {
      // Auth endpoints go to BFF auth routes
      const authPath = endpoint.replace('/auth/', '');
      url = `${BFF_API_URL}/auth/${authPath}`;
    } else if (endpoint.startsWith('/employees')) {
      // Employee endpoints have dedicated BFF routes
      url = `${BFF_API_URL}/employees${endpoint.replace('/employees', '')}`;
    } else {
      // All other endpoints go through the proxy
      url = `${BFF_API_URL}/proxy${endpoint}`;
    }
  } else {
    // Direct mode - use backend URL directly
    url = `${API_BASE_URL}${endpoint}`;
  }

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Only set Content-Type for non-FormData requests
  // FormData needs the browser to set Content-Type with the correct boundary
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  // Public auth endpoints that don't need authorization
  const publicAuthEndpoints = [
    '/auth/login',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/verify-email',
    '/auth/resend-verification',
    '/auth/refresh',
  ];
  const isPublicAuthEndpoint = publicAuthEndpoints.some(ep => endpoint.startsWith(ep));

  // Add auth token if available (for both modes) - but not for public auth endpoints
  if (typeof window !== 'undefined' && !isPublicAuthEndpoint) {
    const token = localStorage.getItem('accessToken');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: USE_BFF ? 'include' : 'same-origin', // Include cookies in BFF mode
  });

  // Handle 401 Unauthorized - try to refresh token or redirect to login
  if (response.status === 401 && !isPublicAuthEndpoint && retryCount === 0) {
    if (USE_BFF) {
      // In BFF mode, try to refresh the token
      const newToken = await refreshAccessToken();
      if (newToken) {
        // Retry the request with new token
        const newHeaders = {
          ...headers,
          'Authorization': `Bearer ${newToken}`,
        };
        return fetchApi<T>(endpoint, { ...options, headers: newHeaders }, retryCount + 1);
      }
      // If refresh failed, redirectToLogin was already called in refreshAccessToken
    } else {
      // In direct mode, redirect to login immediately on 401
      redirectToLogin();
    }
    // Throw error to stop the request chain
    throw new ApiError(401, 'Session expired. Please log in again.', 'SESSION_EXPIRED');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'An error occurred' }));
    throw new ApiError(
      response.status,
      error.message || error.error?.message || 'An error occurred',
      error.error?.code
    );
  }

  const json = await response.json();

  // Unwrap { data: ... } response format from backend, BUT only if there's no meta
  // (paginated responses have { data: [...], meta: {...} } and need the full structure)
  if (json.data !== undefined && json.meta === undefined) {
    return json.data;
  }
  return json;
}

/**
 * Check if BFF mode is enabled
 */
export function isBffEnabled(): boolean {
  return USE_BFF;
}

/**
 * Get the current API mode
 */
export function getApiMode(): 'bff' | 'direct' {
  return USE_BFF ? 'bff' : 'direct';
}

// Search response types
export type SearchType = 'employees' | 'documents' | 'jobs';

export interface SearchResult {
  id: string;
  type: SearchType;
  title: string;
  subtitle: string | null;
  description: string | null;
  rank: number;
  highlight: string | null;
  metadata: Record<string, unknown>;
}

export interface SearchResponse {
  data: SearchResult[];
  meta: {
    query: string;
    type: SearchType | 'all';
    total: number;
    pagination: {
      page: number;
      pageSize: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  };
}

// MFA response types
export interface MfaRequiredResponse {
  requiresMfa: boolean;
  tempToken: string;
  message: string;
}

export interface MfaSetupResponse {
  secret: string;
  qrCodeUrl: string;
  otpauthUrl: string;
}

export interface MfaStatusResponse {
  mfaEnabled: boolean;
}

// Registration response (requires email verification)
export interface RegisterResponse {
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  tokenType?: string;
  requiresEmailVerification: boolean;
  message: string;
}

// Type guard for MFA required response
export function isMfaRequired(response: AuthTokens & { tokenType: string } | MfaRequiredResponse): response is MfaRequiredResponse {
  return 'requiresMfa' in response && response.requiresMfa === true;
}

export const api = {
  auth: {
    login: (data: LoginInput): Promise<(AuthTokens & { tokenType: string }) | MfaRequiredResponse> =>
      fetchApi('/auth/login', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    register: (data: RegisterInput): Promise<RegisterResponse> =>
      fetchApi('/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    verifyEmail: (token: string): Promise<{ message: string; emailVerified: boolean }> =>
      fetchApi('/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),

    resendVerification: (email: string): Promise<{ message: string }> =>
      fetchApi('/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),

    forgotPassword: (email: string): Promise<{ message: string }> =>
      fetchApi('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),

    verifyResetToken: (token: string): Promise<{ valid: boolean; email?: string; error?: string }> =>
      fetchApi('/auth/verify-reset-token', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),

    resetPassword: (token: string, newPassword: string): Promise<{ message: string }> =>
      fetchApi('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword }),
      }),

    refresh: (refreshToken: string): Promise<AuthTokens & { tokenType: string }> =>
      fetchApi('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }),

    me: () =>
      fetchApi('/auth/me'),

    completeMfaLogin: (tempToken: string, token: string): Promise<AuthTokens & { tokenType: string }> =>
      fetchApi('/auth/mfa/complete-login', {
        method: 'POST',
        body: JSON.stringify({ tempToken, token }),
      }),

    switchTenant: (tenantId: string): Promise<AuthTokens & { tokenType: string }> =>
      fetchApi('/auth/switch-tenant', {
        method: 'POST',
        body: JSON.stringify({ tenantId }),
      }),

    completeOnboarding: (data: {
      firstName: string;
      lastName: string;
      jobTitle?: string;
      workPhone?: string;
      companyName?: string;
      timezone?: string;
    }): Promise<{ message: string }> =>
      fetchApi('/auth/complete-onboarding', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  mfa: {
    status: (): Promise<MfaStatusResponse> =>
      fetchApi('/auth/mfa/status'),

    setup: (): Promise<MfaSetupResponse> =>
      fetchApi('/auth/mfa/setup', {
        method: 'POST',
      }),

    verify: (token: string): Promise<{ message: string }> =>
      fetchApi('/auth/mfa/verify', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),

    disable: (token: string): Promise<{ message: string }> =>
      fetchApi('/auth/mfa/disable', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
  },

  employees: {
    list: (params?: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
      departmentId?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.search) searchParams.set('search', params.search);
      if (params?.status) searchParams.set('status', params.status);
      if (params?.departmentId) searchParams.set('departmentId', params.departmentId);

      const query = searchParams.toString();
      return fetchApi(`/employees${query ? `?${query}` : ''}`);
    },

    get: (id: string) =>
      fetchApi(`/employees/${id}`),

    create: (data: Record<string, unknown>) =>
      fetchApi('/employees', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/employees/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchApi(`/employees/${id}`, {
        method: 'DELETE',
      }),

    bulkUpdateManager: (data: { employeeIds: string[]; managerId: string | null }) =>
      fetchApi('/employees/bulk/manager', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    resendInvite: (id: string) =>
      fetchApi(`/employees/${id}/resend-invite`, {
        method: 'POST',
      }),
  },

  departments: {
    list: (params?: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.search) searchParams.set('search', params.search);
      if (params?.status) searchParams.set('status', params.status);

      const query = searchParams.toString();
      return fetchApi(`/departments${query ? `?${query}` : ''}`);
    },

    get: (id: string) =>
      fetchApi(`/departments/${id}`),

    create: (data: Record<string, unknown>) =>
      fetchApi('/departments', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/departments/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchApi(`/departments/${id}`, {
        method: 'DELETE',
      }),
  },

  locations: {
    list: (params?: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
      type?: string;
      country?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.search) searchParams.set('search', params.search);
      if (params?.status) searchParams.set('status', params.status);
      if (params?.type) searchParams.set('type', params.type);
      if (params?.country) searchParams.set('country', params.country);

      const query = searchParams.toString();
      return fetchApi(`/locations${query ? `?${query}` : ''}`);
    },

    get: (id: string) =>
      fetchApi(`/locations/${id}`),

    create: (data: Record<string, unknown>) =>
      fetchApi('/locations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/locations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchApi(`/locations/${id}`, {
        method: 'DELETE',
      }),
  },

  jobs: {
    list: (params?: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
      jobFamilyId?: string;
      flsaStatus?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.search) searchParams.set('search', params.search);
      if (params?.status) searchParams.set('status', params.status);
      if (params?.jobFamilyId) searchParams.set('jobFamilyId', params.jobFamilyId);
      if (params?.flsaStatus) searchParams.set('flsaStatus', params.flsaStatus);

      const query = searchParams.toString();
      return fetchApi(`/jobs${query ? `?${query}` : ''}`);
    },

    get: (id: string) =>
      fetchApi(`/jobs/${id}`),

    create: (data: Record<string, unknown>) =>
      fetchApi('/jobs', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/jobs/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchApi(`/jobs/${id}`, {
        method: 'DELETE',
      }),
  },

  time: {
    list: (params?: {
      page?: number;
      pageSize?: number;
      employeeId?: string;
      startDate?: string;
      endDate?: string;
      status?: string;
      type?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.employeeId) searchParams.set('employeeId', params.employeeId);
      if (params?.startDate) searchParams.set('startDate', params.startDate);
      if (params?.endDate) searchParams.set('endDate', params.endDate);
      if (params?.status) searchParams.set('status', params.status);
      if (params?.type) searchParams.set('type', params.type);

      const query = searchParams.toString();
      return fetchApi(`/time-entries${query ? `?${query}` : ''}`);
    },

    get: (id: string) =>
      fetchApi(`/time-entries/${id}`),

    create: (data: Record<string, unknown>) =>
      fetchApi('/time-entries', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/time-entries/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchApi(`/time-entries/${id}`, {
        method: 'DELETE',
      }),

    status: () =>
      fetchApi('/time-entries/status'),

    weekly: (weekStartDate: string, employeeId?: string) => {
      const searchParams = new URLSearchParams();
      searchParams.set('weekStartDate', weekStartDate);
      if (employeeId) searchParams.set('employeeId', employeeId);
      return fetchApi(`/time-entries/weekly?${searchParams.toString()}`);
    },

    clockIn: (notes?: string) =>
      fetchApi('/time-entries/clock-in', {
        method: 'POST',
        body: JSON.stringify({ notes }),
      }),

    clockOut: (breakMinutes?: number, notes?: string) =>
      fetchApi('/time-entries/clock-out', {
        method: 'POST',
        body: JSON.stringify({ breakMinutes, notes }),
      }),

    approve: (id: string, action: 'APPROVED' | 'REJECTED', reason?: string) =>
      fetchApi(`/time-entries/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ action, reason }),
      }),

    bulkApprove: (ids: string[], action: 'APPROVED' | 'REJECTED', reason?: string) =>
      fetchApi('/time-entries/bulk-approve', {
        method: 'POST',
        body: JSON.stringify({ ids, action, reason }),
      }),

    pendingApprovals: (params?: { page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/time-entries/pending-approvals${query ? `?${query}` : ''}`);
    },
  },

  benefits: {
    // Benefit Plans
    listPlans: (params?: {
      page?: number;
      pageSize?: number;
      type?: string;
      status?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.type) searchParams.set('type', params.type);
      if (params?.status) searchParams.set('status', params.status);

      const query = searchParams.toString();
      return fetchApi(`/benefit-plans${query ? `?${query}` : ''}`);
    },

    getPlan: (id: string) =>
      fetchApi(`/benefit-plans/${id}`),

    createPlan: (data: Record<string, unknown>) =>
      fetchApi('/benefit-plans', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updatePlan: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/benefit-plans/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deletePlan: (id: string) =>
      fetchApi(`/benefit-plans/${id}`, {
        method: 'DELETE',
      }),

    // Enrollments
    listEnrollments: (params?: {
      page?: number;
      pageSize?: number;
      employeeId?: string;
      planId?: string;
      status?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.employeeId) searchParams.set('employeeId', params.employeeId);
      if (params?.planId) searchParams.set('planId', params.planId);
      if (params?.status) searchParams.set('status', params.status);

      const query = searchParams.toString();
      return fetchApi(`/benefit-enrollments${query ? `?${query}` : ''}`);
    },

    myEnrollments: () =>
      fetchApi('/benefit-enrollments/my-enrollments'),

    getEnrollmentsByEmployee: (employeeId: string) =>
      fetchApi(`/benefit-enrollments/employee/${employeeId}`),

    getEnrollment: (id: string) =>
      fetchApi(`/benefit-enrollments/${id}`),

    createEnrollment: (data: {
      employeeId: string;
      planId: string;
      coverageLevel?: string;
      startDate: string;
      endDate?: string;
      dependents?: Array<{
        firstName: string;
        lastName: string;
        relationship: string;
        dateOfBirth?: string;
      }>;
    }) =>
      fetchApi('/benefit-enrollments', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    enrollSelf: (data: {
      planId: string;
      coverageLevel?: string;
      startDate: string;
      endDate?: string;
      dependents?: Array<{
        firstName: string;
        lastName: string;
        relationship: string;
        dateOfBirth?: string;
      }>;
    }) =>
      fetchApi('/benefit-enrollments/enroll-self', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateEnrollment: (id: string, data: {
      coverageLevel?: string;
      status?: string;
      endDate?: string;
    }) =>
      fetchApi(`/benefit-enrollments/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    terminateEnrollment: (id: string, endDate: string) =>
      fetchApi(`/benefit-enrollments/${id}/terminate`, {
        method: 'POST',
        body: JSON.stringify({ endDate }),
      }),

    waiveEnrollment: (employeeId: string, planId: string) =>
      fetchApi('/benefit-enrollments/waive', {
        method: 'POST',
        body: JSON.stringify({ employeeId, planId }),
      }),

    addDependent: (enrollmentId: string, data: {
      firstName: string;
      lastName: string;
      relationship: string;
      dateOfBirth?: string;
    }) =>
      fetchApi(`/benefit-enrollments/${enrollmentId}/dependents`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    removeDependent: (enrollmentId: string, dependentId: string) =>
      fetchApi(`/benefit-enrollments/${enrollmentId}/dependents/${dependentId}`, {
        method: 'DELETE',
      }),
  },

  leave: {
    list: (params?: {
      page?: number;
      pageSize?: number;
      employeeId?: string;
      status?: string;
      type?: string;
      startDate?: string;
      endDate?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.employeeId) searchParams.set('employeeId', params.employeeId);
      if (params?.status) searchParams.set('status', params.status);
      if (params?.type) searchParams.set('type', params.type);
      if (params?.startDate) searchParams.set('startDate', params.startDate);
      if (params?.endDate) searchParams.set('endDate', params.endDate);

      const query = searchParams.toString();
      return fetchApi(`/leave-requests${query ? `?${query}` : ''}`);
    },

    myRequests: (params?: {
      page?: number;
      pageSize?: number;
      status?: string;
      type?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.set('status', params.status);
      if (params?.type) searchParams.set('type', params.type);

      const query = searchParams.toString();
      return fetchApi(`/leave-requests/my-requests${query ? `?${query}` : ''}`);
    },

    myBalances: (year?: number) => {
      const query = year ? `?year=${year}` : '';
      return fetchApi(`/leave-requests/my-balances${query}`);
    },

    employeeBalances: (employeeId: string, year?: number) => {
      const query = year ? `?year=${year}` : '';
      return fetchApi(`/leave-requests/balances/${employeeId}${query}`);
    },

    get: (id: string) =>
      fetchApi(`/leave-requests/${id}`),

    create: (data: {
      type: string;
      startDate: string;
      endDate: string;
      totalDays: number;
      reason?: string;
    }) =>
      fetchApi('/leave-requests', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    createForEmployee: (employeeId: string, data: {
      type: string;
      startDate: string;
      endDate: string;
      totalDays: number;
      reason?: string;
    }) =>
      fetchApi(`/leave-requests/${employeeId}`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: {
      type?: string;
      startDate?: string;
      endDate?: string;
      totalDays?: number;
      reason?: string;
    }) =>
      fetchApi(`/leave-requests/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    review: (id: string, data: { action: 'APPROVE' | 'REJECT'; notes?: string }) =>
      fetchApi(`/leave-requests/${id}/review`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    cancel: (id: string) =>
      fetchApi(`/leave-requests/${id}`, {
        method: 'DELETE',
      }),
  },

  tenants: {
    current: () =>
      fetchApi('/tenants/current'),
  },

  performance: {
    // Review Cycles
    listCycles: (params?: {
      page?: number;
      pageSize?: number;
      status?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.set('status', params.status);

      const query = searchParams.toString();
      return fetchApi(`/review-cycles${query ? `?${query}` : ''}`);
    },

    getCycle: (id: string) =>
      fetchApi(`/review-cycles/${id}`),

    createCycle: (data: {
      name: string;
      description?: string;
      startDate: string;
      endDate: string;
      status?: string;
    }) =>
      fetchApi('/review-cycles', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateCycle: (id: string, data: {
      name?: string;
      description?: string;
      startDate?: string;
      endDate?: string;
      status?: string;
    }) =>
      fetchApi(`/review-cycles/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deleteCycle: (id: string) =>
      fetchApi(`/review-cycles/${id}`, {
        method: 'DELETE',
      }),

    // Reviews
    listReviews: (params?: {
      page?: number;
      pageSize?: number;
      cycleId?: string;
      employeeId?: string;
      reviewerId?: string;
      status?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.cycleId) searchParams.set('cycleId', params.cycleId);
      if (params?.employeeId) searchParams.set('employeeId', params.employeeId);
      if (params?.reviewerId) searchParams.set('reviewerId', params.reviewerId);
      if (params?.status) searchParams.set('status', params.status);

      const query = searchParams.toString();
      return fetchApi(`/reviews${query ? `?${query}` : ''}`);
    },

    myReviews: (params?: {
      page?: number;
      pageSize?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

      const query = searchParams.toString();
      return fetchApi(`/reviews/my${query ? `?${query}` : ''}`);
    },

    getReview: (id: string) =>
      fetchApi(`/reviews/${id}`),

    createReview: (data: {
      cycleId: string;
      employeeId: string;
      reviewerId: string;
      status?: string;
    }) =>
      fetchApi('/reviews', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateReview: (id: string, data: {
      status?: string;
      selfRating?: number;
      selfComments?: string;
      managerRating?: number;
      managerComments?: string;
      overallRating?: number;
      strengths?: string;
      improvements?: string;
    }) =>
      fetchApi(`/reviews/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deleteReview: (id: string) =>
      fetchApi(`/reviews/${id}`, {
        method: 'DELETE',
      }),

    // Goals
    listGoals: (params?: {
      page?: number;
      pageSize?: number;
      employeeId?: string;
      reviewId?: string;
      status?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.employeeId) searchParams.set('employeeId', params.employeeId);
      if (params?.reviewId) searchParams.set('reviewId', params.reviewId);
      if (params?.status) searchParams.set('status', params.status);

      const query = searchParams.toString();
      return fetchApi(`/goals${query ? `?${query}` : ''}`);
    },

    myGoals: (params?: {
      page?: number;
      pageSize?: number;
      status?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.set('status', params.status);

      const query = searchParams.toString();
      return fetchApi(`/goals/my${query ? `?${query}` : ''}`);
    },

    getGoal: (id: string) =>
      fetchApi(`/goals/${id}`),

    createGoal: (data: {
      employeeId: string;
      reviewId?: string;
      title: string;
      description?: string;
      dueDate?: string;
      progress?: number;
      status?: string;
    }) =>
      fetchApi('/goals', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateGoal: (id: string, data: {
      title?: string;
      description?: string;
      dueDate?: string;
      progress?: number;
      status?: string;
      reviewId?: string;
    }) =>
      fetchApi(`/goals/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deleteGoal: (id: string) =>
      fetchApi(`/goals/${id}`, {
        method: 'DELETE',
      }),

    teamGoals: (params?: {
      page?: number;
      pageSize?: number;
      status?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.set('status', params.status);

      const query = searchParams.toString();
      return fetchApi(`/goals/team${query ? `?${query}` : ''}`);
    },

    reviewGoal: (id: string, data: { action: 'approve' | 'reject' }) =>
      fetchApi(`/goals/${id}/review`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
  },

  reports: {
    getHeadcount: (params?: {
      startDate?: string;
      endDate?: string;
      dateRange?: string;
      departmentId?: string;
      locationId?: string;
      employmentType?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.startDate) searchParams.set('startDate', params.startDate);
      if (params?.endDate) searchParams.set('endDate', params.endDate);
      if (params?.dateRange) searchParams.set('dateRange', params.dateRange);
      if (params?.departmentId) searchParams.set('departmentId', params.departmentId);
      if (params?.locationId) searchParams.set('locationId', params.locationId);
      if (params?.employmentType) searchParams.set('employmentType', params.employmentType);

      const query = searchParams.toString();
      return fetchApi(`/reports/headcount${query ? `?${query}` : ''}`);
    },

    getTurnover: (params?: {
      startDate?: string;
      endDate?: string;
      dateRange?: string;
      departmentId?: string;
      locationId?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.startDate) searchParams.set('startDate', params.startDate);
      if (params?.endDate) searchParams.set('endDate', params.endDate);
      if (params?.dateRange) searchParams.set('dateRange', params.dateRange);
      if (params?.departmentId) searchParams.set('departmentId', params.departmentId);
      if (params?.locationId) searchParams.set('locationId', params.locationId);

      const query = searchParams.toString();
      return fetchApi(`/reports/turnover${query ? `?${query}` : ''}`);
    },

    getDemographics: (params?: {
      departmentId?: string;
      locationId?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.departmentId) searchParams.set('departmentId', params.departmentId);
      if (params?.locationId) searchParams.set('locationId', params.locationId);

      const query = searchParams.toString();
      return fetchApi(`/reports/demographics${query ? `?${query}` : ''}`);
    },

    getCompensation: (params?: {
      departmentId?: string;
      locationId?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.departmentId) searchParams.set('departmentId', params.departmentId);
      if (params?.locationId) searchParams.set('locationId', params.locationId);

      const query = searchParams.toString();
      return fetchApi(`/reports/compensation${query ? `?${query}` : ''}`);
    },

    getTimeOff: (params?: {
      startDate?: string;
      endDate?: string;
      dateRange?: string;
      departmentId?: string;
      locationId?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.startDate) searchParams.set('startDate', params.startDate);
      if (params?.endDate) searchParams.set('endDate', params.endDate);
      if (params?.dateRange) searchParams.set('dateRange', params.dateRange);
      if (params?.departmentId) searchParams.set('departmentId', params.departmentId);
      if (params?.locationId) searchParams.set('locationId', params.locationId);

      const query = searchParams.toString();
      return fetchApi(`/reports/time-off${query ? `?${query}` : ''}`);
    },

    getHiring: (params?: {
      startDate?: string;
      endDate?: string;
      dateRange?: string;
      departmentId?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.startDate) searchParams.set('startDate', params.startDate);
      if (params?.endDate) searchParams.set('endDate', params.endDate);
      if (params?.dateRange) searchParams.set('dateRange', params.dateRange);
      if (params?.departmentId) searchParams.set('departmentId', params.departmentId);

      const query = searchParams.toString();
      return fetchApi(`/reports/hiring${query ? `?${query}` : ''}`);
    },

    // Report Builder
    builder: {
      getEntities: () => fetchApi('/reports/entities'),

      getEntityFields: (entityType: string) =>
        fetchApi(`/reports/entities/${entityType}/fields`),

      listDefinitions: (params?: { page?: number; pageSize?: number }) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.set('page', String(params.page));
        if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
        const query = searchParams.toString();
        return fetchApi(`/reports/builder/definitions${query ? `?${query}` : ''}`);
      },

      getDefinition: (id: string) =>
        fetchApi(`/reports/builder/definitions/${id}`),

      createDefinition: (data: {
        name: string;
        description?: string;
        config: Record<string, unknown>;
        visibility?: string;
        category?: string;
      }) =>
        fetchApi('/reports/builder/definitions', {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      updateDefinition: (id: string, data: Record<string, unknown>) =>
        fetchApi(`/reports/builder/definitions/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        }),

      deleteDefinition: (id: string) =>
        fetchApi(`/reports/builder/definitions/${id}`, {
          method: 'DELETE',
        }),

      preview: (data: { config: Record<string, unknown>; limit?: number }) =>
        fetchApi('/reports/builder/preview', {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      execute: (id: string, params?: { page?: number; pageSize?: number }) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.set('page', String(params.page));
        if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
        const query = searchParams.toString();
        return fetchApi(`/reports/builder/definitions/${id}/execute${query ? `?${query}` : ''}`, {
          method: 'POST',
        });
      },

      export: (id: string, format: string) =>
        fetchApi(`/reports/builder/definitions/${id}/export`, {
          method: 'POST',
          body: JSON.stringify({ format }),
        }),
    },

    // Report Schedules
    schedules: {
      list: (params?: { page?: number; pageSize?: number }) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.set('page', String(params.page));
        if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
        const query = searchParams.toString();
        return fetchApi(`/reports/schedules${query ? `?${query}` : ''}`);
      },

      get: (id: string) => fetchApi(`/reports/schedules/${id}`),

      create: (data: Record<string, unknown>) =>
        fetchApi('/reports/schedules', {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      update: (id: string, data: Record<string, unknown>) =>
        fetchApi(`/reports/schedules/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        }),

      delete: (id: string) =>
        fetchApi(`/reports/schedules/${id}`, {
          method: 'DELETE',
        }),

      execute: (id: string) =>
        fetchApi(`/reports/schedules/${id}/execute`, {
          method: 'POST',
        }),

      history: (id: string) =>
        fetchApi(`/reports/schedules/${id}/history`),
    },
  },

  payroll: {
    // Payroll Runs
    listRuns: (params?: {
      page?: number;
      pageSize?: number;
      status?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.set('status', params.status);

      const query = searchParams.toString();
      return fetchApi(`/payroll-runs${query ? `?${query}` : ''}`);
    },

    getRun: (id: string) =>
      fetchApi(`/payroll-runs/${id}`),

    createRun: (data: {
      periodStart: string;
      periodEnd: string;
      payDate: string;
    }) =>
      fetchApi('/payroll-runs', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateRun: (id: string, data: {
      periodStart?: string;
      periodEnd?: string;
      payDate?: string;
      status?: string;
    }) =>
      fetchApi(`/payroll-runs/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deleteRun: (id: string) =>
      fetchApi(`/payroll-runs/${id}`, {
        method: 'DELETE',
      }),

    processPayroll: (id: string) =>
      fetchApi(`/payroll-runs/${id}/process`, {
        method: 'POST',
      }),

    approvePayroll: (id: string) =>
      fetchApi(`/payroll-runs/${id}/approve`, {
        method: 'POST',
      }),

    markAsPaid: (id: string) =>
      fetchApi(`/payroll-runs/${id}/pay`, {
        method: 'POST',
      }),

    cancelPayroll: (id: string) =>
      fetchApi(`/payroll-runs/${id}/cancel`, {
        method: 'POST',
      }),

    // Pay Stubs
    listStubs: (params?: {
      page?: number;
      pageSize?: number;
      payrollRunId?: string;
      employeeId?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.payrollRunId) searchParams.set('payrollRunId', params.payrollRunId);
      if (params?.employeeId) searchParams.set('employeeId', params.employeeId);

      const query = searchParams.toString();
      return fetchApi(`/pay-stubs${query ? `?${query}` : ''}`);
    },

    getStub: (id: string) =>
      fetchApi(`/pay-stubs/${id}`),

    getEmployeeStubs: (employeeId: string, params?: {
      page?: number;
      pageSize?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

      const query = searchParams.toString();
      return fetchApi(`/pay-stubs/employee/${employeeId}${query ? `?${query}` : ''}`);
    },

    getRunSummary: (payrollRunId: string) =>
      fetchApi(`/pay-stubs/summary/${payrollRunId}`),

    // Year-end tax forms
    getYearEndChecklistForYear: (year: number) =>
      fetchApi(`/payroll/year-end-checklist/${year}`),

    getTaxForms: (params?: { year?: number; employeeId?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.year) searchParams.set('year', params.year.toString());
      if (params?.employeeId) searchParams.set('employeeId', params.employeeId);
      const query = searchParams.toString();
      return fetchApi(`/payroll/tax-forms${query ? `?${query}` : ''}`);
    },

    generateTaxForms: (year: number) =>
      fetchApi(`/payroll/tax-forms/generate/${year}`, {
        method: 'POST',
      }),

    bulkGenerateW2s: (year: number, options?: { includeTerminated?: boolean }) =>
      fetchApi(`/payroll/w2s/bulk-generate/${year}`, {
        method: 'POST',
        body: JSON.stringify(options || {}),
      }),

    bulkGenerate1099s: (year: number, options?: { includeTerminated?: boolean }) =>
      fetchApi(`/payroll/1099s/bulk-generate/${year}`, {
        method: 'POST',
        body: JSON.stringify(options || {}),
      }),

    getEmployeeTaxForms: (employeeId: string, year?: number) => {
      const query = year ? `?year=${year}` : '';
      return fetchApi(`/payroll/tax-forms/employee/${employeeId}${query}`);
    },

    downloadTaxForm: (formId: string) =>
      fetchApi(`/payroll/tax-forms/${formId}/download`),
  },

  compensation: {
    // Salary Bands
    listSalaryBands: (params?: {
      page?: number;
      pageSize?: number;
      status?: string;
      jobId?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.set('status', params.status);
      if (params?.jobId) searchParams.set('jobId', params.jobId);

      const query = searchParams.toString();
      return fetchApi(`/compensation/salary-bands${query ? `?${query}` : ''}`);
    },

    getSalaryBand: (id: string) =>
      fetchApi(`/compensation/salary-bands/${id}`),

    getSalaryBandsByJob: (jobId: string) =>
      fetchApi(`/compensation/salary-bands/job/${jobId}`),

    createSalaryBand: (data: {
      jobId: string;
      level?: string;
      minSalary: number;
      midSalary: number;
      maxSalary: number;
      currency?: string;
      effectiveDate: string;
      status?: string;
    }) =>
      fetchApi('/compensation/salary-bands', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateSalaryBand: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/compensation/salary-bands/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deleteSalaryBand: (id: string) =>
      fetchApi(`/compensation/salary-bands/${id}`, {
        method: 'DELETE',
      }),

    // Compensation Reviews
    listReviews: (params?: {
      page?: number;
      pageSize?: number;
      status?: string;
      employeeId?: string;
      type?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.set('status', params.status);
      if (params?.employeeId) searchParams.set('employeeId', params.employeeId);
      if (params?.type) searchParams.set('type', params.type);

      const query = searchParams.toString();
      return fetchApi(`/compensation/reviews${query ? `?${query}` : ''}`);
    },

    getReview: (id: string) =>
      fetchApi(`/compensation/reviews/${id}`),

    createReview: (data: {
      employeeId: string;
      reviewerId?: string;
      type: string;
      currentSalary: number;
      proposedSalary: number;
      effectiveDate: string;
      notes?: string;
      status?: string;
    }) =>
      fetchApi('/compensation/reviews', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateReview: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/compensation/reviews/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    approveReview: (id: string) =>
      fetchApi(`/compensation/reviews/${id}/approve`, {
        method: 'POST',
      }),

    applyReview: (id: string) =>
      fetchApi(`/compensation/reviews/${id}/apply`, {
        method: 'POST',
      }),

    // Bonuses
    listBonuses: (params?: {
      page?: number;
      pageSize?: number;
      status?: string;
      employeeId?: string;
      type?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.set('status', params.status);
      if (params?.employeeId) searchParams.set('employeeId', params.employeeId);
      if (params?.type) searchParams.set('type', params.type);

      const query = searchParams.toString();
      return fetchApi(`/compensation/bonuses${query ? `?${query}` : ''}`);
    },

    getBonus: (id: string) =>
      fetchApi(`/compensation/bonuses/${id}`),

    createBonus: (data: {
      employeeId: string;
      type: string;
      amount: number;
      currency?: string;
      reason?: string;
      effectiveDate: string;
      status?: string;
    }) =>
      fetchApi('/compensation/bonuses', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateBonus: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/compensation/bonuses/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    // Employee History
    getEmployeeHistory: (employeeId: string) =>
      fetchApi(`/compensation/employee/${employeeId}/history`),

    // Analytics
    getAnalytics: (params?: { departmentId?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.departmentId) searchParams.set('departmentId', params.departmentId);

      const query = searchParams.toString();
      return fetchApi(`/compensation/analytics${query ? `?${query}` : ''}`);
    },
  },

  settings: {
    getCompany: () =>
      fetchApi('/settings/company'),

    updateCompany: (data: Record<string, unknown>) =>
      fetchApi('/settings/company', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    getSecurity: () =>
      fetchApi('/settings/security'),

    updateSecurity: (data: Record<string, unknown>) =>
      fetchApi('/settings/security', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    getUsers: () =>
      fetchApi('/settings/users'),

    inviteUser: (data: { email: string; role: string }) =>
      fetchApi('/settings/users/invite', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateUserRole: (id: string, data: { role: string }) =>
      fetchApi(`/settings/users/${id}/role`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    removeUser: (id: string) =>
      fetchApi(`/settings/users/${id}`, {
        method: 'DELETE',
      }),
  },

  apiKeys: {
    list: () =>
      fetchApi('/api-keys'),

    create: (data: { name: string; scopes: string[]; expiresAt?: string }) =>
      fetchApi('/api-keys', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    revoke: (id: string) =>
      fetchApi(`/api-keys/${id}`, {
        method: 'DELETE',
      }),
  },

  health: {
    check: () =>
      fetchApi('/health'),

    ready: () =>
      fetchApi('/health/ready'),
  },

  search: {
    unified: (params: {
      q: string;
      type?: 'all' | 'employees' | 'documents' | 'jobs';
      page?: number;
      pageSize?: number;
    }): Promise<SearchResponse> => {
      const searchParams = new URLSearchParams();
      searchParams.set('q', params.q);
      if (params.type) searchParams.set('type', params.type);
      if (params.page) searchParams.set('page', params.page.toString());
      if (params.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      return fetchApi(`/search?${searchParams.toString()}`);
    },

    employees: (params: {
      q: string;
      page?: number;
      pageSize?: number;
    }): Promise<SearchResponse> => {
      const searchParams = new URLSearchParams();
      searchParams.set('q', params.q);
      if (params.page) searchParams.set('page', params.page.toString());
      if (params.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      return fetchApi(`/search/employees?${searchParams.toString()}`);
    },

    documents: (params: {
      q: string;
      page?: number;
      pageSize?: number;
    }): Promise<SearchResponse> => {
      const searchParams = new URLSearchParams();
      searchParams.set('q', params.q);
      if (params.page) searchParams.set('page', params.page.toString());
      if (params.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      return fetchApi(`/search/documents?${searchParams.toString()}`);
    },

    jobs: (params: {
      q: string;
      page?: number;
      pageSize?: number;
    }): Promise<SearchResponse> => {
      const searchParams = new URLSearchParams();
      searchParams.set('q', params.q);
      if (params.page) searchParams.set('page', params.page.toString());
      if (params.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      return fetchApi(`/search/jobs?${searchParams.toString()}`);
    },
  },

  onboarding: {
    // Templates
    listTemplates: (params?: {
      page?: number;
      pageSize?: number;
      status?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.set('status', params.status);

      const query = searchParams.toString();
      return fetchApi(`/onboarding/templates${query ? `?${query}` : ''}`);
    },

    getTemplate: (id: string) =>
      fetchApi(`/onboarding/templates/${id}`),

    createTemplate: (data: {
      name: string;
      description?: string;
      steps?: Array<{
        title: string;
        description?: string;
        category?: string;
        daysUntilDue?: number;
      }>;
      isDefault?: boolean;
    }) =>
      fetchApi('/onboarding/templates', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateTemplate: (id: string, data: {
      name?: string;
      description?: string;
      steps?: Array<{
        title: string;
        description?: string;
        category?: string;
        daysUntilDue?: number;
      }>;
      isDefault?: boolean;
      status?: string;
    }) =>
      fetchApi(`/onboarding/templates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deleteTemplate: (id: string) =>
      fetchApi(`/onboarding/templates/${id}`, {
        method: 'DELETE',
      }),

    // Checklists
    listChecklists: (params?: {
      page?: number;
      pageSize?: number;
      status?: string;
      employeeId?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.set('status', params.status);
      if (params?.employeeId) searchParams.set('employeeId', params.employeeId);

      const query = searchParams.toString();
      return fetchApi(`/onboarding/checklists${query ? `?${query}` : ''}`);
    },

    getChecklist: (id: string) =>
      fetchApi(`/onboarding/checklists/${id}`),

    createChecklist: (data: {
      employeeId: string;
      templateId?: string;
      startDate?: string;
    }) =>
      fetchApi('/onboarding/checklists', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateChecklist: (id: string, data: {
      status?: string;
      startDate?: string;
      completedDate?: string;
    }) =>
      fetchApi(`/onboarding/checklists/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    // Tasks
    getChecklistTasks: (checklistId: string) =>
      fetchApi(`/onboarding/checklists/${checklistId}/tasks`),

    createTask: (checklistId: string, data: {
      title: string;
      description?: string;
      category?: string;
      assigneeId?: string;
      dueDate?: string;
      order?: number;
    }) =>
      fetchApi(`/onboarding/checklists/${checklistId}/tasks`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateTask: (taskId: string, data: {
      title?: string;
      description?: string;
      category?: string;
      assigneeId?: string;
      dueDate?: string;
      status?: string;
      order?: number;
    }) =>
      fetchApi(`/onboarding/tasks/${taskId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    // Start onboarding for employee
    startOnboarding: (employeeId: string, data?: {
      templateId?: string;
      startDate?: string;
    }) =>
      fetchApi(`/onboarding/employees/${employeeId}/start`, {
        method: 'POST',
        body: JSON.stringify(data || {}),
      }),
  },

  documents: {
    list: (params?: {
      page?: number;
      pageSize?: number;
      employeeId?: string;
      type?: string;
      isConfidential?: boolean;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.employeeId) searchParams.set('employeeId', params.employeeId);
      if (params?.type) searchParams.set('type', params.type);
      if (params?.isConfidential !== undefined) searchParams.set('isConfidential', params.isConfidential.toString());

      const query = searchParams.toString();
      return fetchApi(`/documents${query ? `?${query}` : ''}`);
    },

    get: (id: string) =>
      fetchApi(`/documents/${id}`),

    getByEmployee: (employeeId: string, params?: {
      page?: number;
      pageSize?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

      const query = searchParams.toString();
      return fetchApi(`/documents/employee/${employeeId}${query ? `?${query}` : ''}`);
    },

    upload: async (data: {
      file: File;
      employeeId?: string;
      type: string;
      name: string;
      description?: string;
      isConfidential?: boolean;
    }) => {
      const formData = new FormData();
      formData.append('file', data.file);
      formData.append('type', data.type);
      formData.append('name', data.name);
      if (data.employeeId) formData.append('employeeId', data.employeeId);
      if (data.description) formData.append('description', data.description);
      if (data.isConfidential !== undefined) formData.append('isConfidential', String(data.isConfidential));

      // Determine upload URL based on mode
      const uploadUrl = USE_BFF
        ? '/api/proxy/documents/upload'
        : `${API_BASE_URL}/documents/upload`;

      const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: formData,
        credentials: USE_BFF ? 'include' : 'same-origin',
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'An error occurred' }));
        throw new Error(error.message || error.error?.message || 'An error occurred');
      }

      return response.json();
    },

    getPresignedUploadUrl: (data: {
      employeeId?: string;
      type: string;
      name: string;
      filename: string;
      mimeType: string;
      fileSize: number;
      description?: string;
      isConfidential?: boolean;
    }) =>
      fetchApi('/documents/presigned-url', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    getDownloadUrl: (id: string) =>
      fetchApi(`/documents/${id}/download`),

    update: (id: string, data: {
      type?: string;
      name?: string;
      description?: string;
      isConfidential?: boolean;
    }) =>
      fetchApi(`/documents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchApi(`/documents/${id}`, {
        method: 'DELETE',
      }),
  },

  notifications: {
    list: (params?: {
      page?: number;
      pageSize?: number;
      status?: string;
      type?: string;
      unreadOnly?: boolean;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.set('status', params.status);
      if (params?.type) searchParams.set('type', params.type);
      if (params?.unreadOnly) searchParams.set('unreadOnly', 'true');

      const query = searchParams.toString();
      return fetchApi(`/notifications${query ? `?${query}` : ''}`);
    },

    get: (id: string) =>
      fetchApi(`/notifications/${id}`),

    getUnreadCount: () =>
      fetchApi('/notifications/unread-count'),

    markAsRead: (id: string) =>
      fetchApi(`/notifications/${id}/read`, {
        method: 'PATCH',
      }),

    markAllAsRead: () =>
      fetchApi('/notifications/read-all', {
        method: 'PATCH',
      }),

    delete: (id: string) =>
      fetchApi(`/notifications/${id}`, {
        method: 'DELETE',
      }),

    getPreferences: () =>
      fetchApi('/notifications/preferences'),

    updatePreferences: (data: {
      preferences: Array<{
        channel: string;
        type: string;
        enabled: boolean;
      }>;
    }) =>
      fetchApi('/notifications/preferences', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    send: (data: {
      userIds: string[];
      employeeIds?: string[];
      type: string;
      title: string;
      message: string;
      data?: Record<string, unknown>;
      channel?: string;
      sendEmail?: boolean;
    }) =>
      fetchApi('/notifications/send', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  approvals: {
    pending: (params?: {
      page?: number;
      pageSize?: number;
      type?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.type) searchParams.set('type', params.type);
      const query = searchParams.toString();
      return fetchApi(`/approvals/pending${query ? `?${query}` : ''}`);
    },

    summary: () => fetchApi('/approvals/summary'),

    myRequests: (params?: {
      page?: number;
      pageSize?: number;
      status?: string;
      type?: string;
      startDate?: string;
      endDate?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.set('status', params.status);
      if (params?.type) searchParams.set('type', params.type);
      if (params?.startDate) searchParams.set('startDate', params.startDate);
      if (params?.endDate) searchParams.set('endDate', params.endDate);
      const query = searchParams.toString();
      return fetchApi(`/approvals/requests${query ? `?${query}` : ''}`);
    },

    get: (id: string) => fetchApi(`/approvals/${id}`),

    approve: (id: string, data: { comments?: string }) =>
      fetchApi(`/approvals/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ action: 'APPROVE', ...data }),
      }),

    reject: (id: string, data: { comments?: string }) =>
      fetchApi(`/approvals/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ action: 'REJECT', ...data }),
      }),

    delegate: (id: string, data: { toUserId: string; reason?: string }) =>
      fetchApi(`/approvals/${id}/delegate`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    escalate: (id: string, data: { reason?: string }) =>
      fetchApi(`/approvals/${id}/escalate`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    cancel: (id: string, reason: string) =>
      fetchApi(`/approvals/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),

    bulkAction: (data: {
      approvalIds: string[];
      action: 'APPROVE' | 'REJECT';
      comments?: string;
    }) =>
      fetchApi('/approvals/bulk/action', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Delegations
    delegations: (params?: { active?: boolean; page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.active !== undefined) searchParams.set('active', params.active.toString());
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/approvals/delegations${query ? `?${query}` : ''}`);
    },

    delegationsToMe: (params?: { active?: boolean; page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.active !== undefined) searchParams.set('active', params.active.toString());
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/approvals/delegations/to-me${query ? `?${query}` : ''}`);
    },

    getDelegation: (id: string) => fetchApi(`/approvals/delegations/${id}`),

    createDelegation: (data: {
      delegateId: string;
      requestTypes?: string[];
      startDate: string;
      endDate?: string;
      reason?: string;
    }) =>
      fetchApi('/approvals/delegations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateDelegation: (id: string, data: {
      delegateId?: string;
      requestTypes?: string[];
      startDate?: string;
      endDate?: string;
      isActive?: boolean;
    }) =>
      fetchApi(`/approvals/delegations/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    deleteDelegation: (id: string) =>
      fetchApi(`/approvals/delegations/${id}`, {
        method: 'DELETE',
      }),
  },

  integrations: {
    list: (params?: {
      page?: number;
      pageSize?: number;
      type?: string;
      status?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.type) searchParams.set('type', params.type);
      if (params?.status) searchParams.set('status', params.status);

      const query = searchParams.toString();
      return fetchApi(`/integrations${query ? `?${query}` : ''}`);
    },

    get: (id: string) =>
      fetchApi(`/integrations/${id}`),

    create: (data: Record<string, unknown>) =>
      fetchApi('/integrations', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/integrations/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchApi(`/integrations/${id}`, {
        method: 'DELETE',
      }),

    test: (id: string) =>
      fetchApi(`/integrations/${id}/test`, {
        method: 'POST',
      }),

    sync: (id: string) =>
      fetchApi(`/integrations/${id}/sync`, {
        method: 'POST',
      }),

    getLogs: (id: string, params?: { page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

      const query = searchParams.toString();
      return fetchApi(`/integrations/${id}/logs${query ? `?${query}` : ''}`);
    },
  },

  webhooks: {
    list: (params?: {
      page?: number;
      pageSize?: number;
      status?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.set('status', params.status);

      const query = searchParams.toString();
      return fetchApi(`/webhooks${query ? `?${query}` : ''}`);
    },

    get: (id: string) =>
      fetchApi(`/webhooks/${id}`),

    getEvents: () =>
      fetchApi('/webhooks/events'),

    create: (data: Record<string, unknown>) =>
      fetchApi('/webhooks', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/webhooks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchApi(`/webhooks/${id}`, {
        method: 'DELETE',
      }),

    getLogs: (id: string, params?: { page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

      const query = searchParams.toString();
      return fetchApi(`/webhooks/${id}/logs${query ? `?${query}` : ''}`);
    },
  },

  featureFlags: {
    // Admin endpoints (platform-wide)
    list: (): Promise<{ data: FeatureFlag[]; meta: { total: number } }> =>
      fetchApi('/feature-flags'),

    get: (id: string): Promise<FeatureFlag> =>
      fetchApi(`/feature-flags/${id}`),

    create: (data: CreateFeatureFlagInput): Promise<FeatureFlag> =>
      fetchApi('/feature-flags', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: UpdateFeatureFlagInput): Promise<FeatureFlag> =>
      fetchApi(`/feature-flags/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string): Promise<{ success: boolean }> =>
      fetchApi(`/feature-flags/${id}`, {
        method: 'DELETE',
      }),

    // Tenant-scoped endpoints
    getAllForTenant: (): Promise<Record<string, unknown>> =>
      fetchApi('/feature-flags/tenant/all'),

    getByKey: (key: string): Promise<{ key: string; value: unknown }> =>
      fetchApi(`/feature-flags/key/${key}`),

    isEnabled: (key: string): Promise<{ key: string; enabled: boolean }> =>
      fetchApi(`/feature-flags/key/${key}/enabled`),

    // Override management
    setOverride: (key: string, value: unknown): Promise<{ key: string; tenantId: string; value: unknown }> =>
      fetchApi(`/feature-flags/key/${key}/override`, {
        method: 'PUT',
        body: JSON.stringify({ value }),
      }),

    removeOverride: (key: string): Promise<{ success: boolean }> =>
      fetchApi(`/feature-flags/key/${key}/override`, {
        method: 'DELETE',
      }),
  },

recruiting: {
    // Interviews
    scheduleInterview: (data: ScheduleInterviewInput) =>
      fetchApi('/recruiting/interviews/schedule', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    rescheduleInterview: (id: string, data: { scheduledAt: string; duration?: number; reason?: string }) =>
      fetchApi(`/recruiting/interviews/${id}/reschedule`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    cancelInterview: (id: string, data: { reason: string }) =>
      fetchApi(`/recruiting/interviews/${id}`, {
        method: 'DELETE',
        body: JSON.stringify(data),
      }),

    updateInterview: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/recruiting/interviews/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    getInterview: (id: string) =>
      fetchApi(`/recruiting/interviews/${id}`),

    confirmInterview: (id: string, data: { confirmed: boolean; notes?: string }) =>
      fetchApi(`/recruiting/interviews/${id}/confirm`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    sendInterviewInvite: (id: string) =>
      fetchApi(`/recruiting/interviews/${id}/invite`, {
        method: 'POST',
      }),

    // Availability
    getInterviewerAvailability: (params: {
      interviewerId: string;
      startDate: string;
      endDate: string;
      timezone?: string;
    }) => {
      const searchParams = new URLSearchParams();
      searchParams.set('interviewerId', params.interviewerId);
      searchParams.set('startDate', params.startDate);
      searchParams.set('endDate', params.endDate);
      if (params.timezone) searchParams.set('timezone', params.timezone);
      return fetchApi(`/recruiting/interviews/availability?${searchParams.toString()}`);
    },

    findAvailableSlots: (data: {
      interviewerIds: string[];
      duration: number;
      startDate: string;
      endDate: string;
      timezone?: string;
      maxSlots?: number;
    }) =>
      fetchApi('/recruiting/interviews/slots', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Interview Lists
    getUpcomingInterviews: (params?: { page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/recruiting/interviews/upcoming${query ? `?${query}` : ''}`);
    },

    getInterviewsByCandidate: (candidateId: string, params?: { page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/recruiting/interviews/candidate/${candidateId}${query ? `?${query}` : ''}`);
    },

    // Feedback
    submitFeedback: (interviewId: string, data: InterviewFeedbackInput) =>
      fetchApi(`/recruiting/interviews/${interviewId}/feedback`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateFeedback: (feedbackId: string, data: Partial<InterviewFeedbackInput>) =>
      fetchApi(`/recruiting/interviews/feedback/${feedbackId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    getFeedback: (interviewId: string) =>
      fetchApi(`/recruiting/interviews/${interviewId}/feedback`),

    getScorecard: (interviewId: string) =>
      fetchApi(`/recruiting/interviews/${interviewId}/scorecard`),

    getPendingFeedback: (params?: { page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/recruiting/interviews/pending-feedback${query ? `?${query}` : ''}`);
    },

    getCandidateFeedbackSummary: (candidateId: string) =>
      fetchApi(`/recruiting/candidates/${candidateId}/feedback-summary`),

    getCandidateOverallScore: (candidateId: string) =>
      fetchApi(`/recruiting/candidates/${candidateId}/overall-score`),

    // Interview Types
    getInterviewTypes: (includeInactive?: boolean) => {
      const query = includeInactive ? '?includeInactive=true' : '';
      return fetchApi(`/recruiting/interview-types${query}`);
    },

    getInterviewType: (id: string) =>
      fetchApi(`/recruiting/interview-types/${id}`),

    createInterviewType: (data: Record<string, unknown>) =>
      fetchApi('/recruiting/interview-types', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateInterviewType: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/recruiting/interview-types/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deleteInterviewType: (id: string) =>
      fetchApi(`/recruiting/interview-types/${id}`, {
        method: 'DELETE',
      }),

    // Scorecard Templates
    getScorecardTemplates: () =>
      fetchApi('/recruiting/interview-types/scorecard-templates'),

    getScorecardTemplate: (id: string) =>
      fetchApi(`/recruiting/interview-types/scorecard-templates/${id}`),

    createScorecardTemplate: (data: Record<string, unknown>) =>
      fetchApi('/recruiting/interview-types/scorecard-templates', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateScorecardTemplate: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/recruiting/interview-types/scorecard-templates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deleteScorecardTemplate: (id: string) =>
      fetchApi(`/recruiting/interview-types/scorecard-templates/${id}`, {
        method: 'DELETE',
      }),

    // Job Postings
    listJobPostings: (params?: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
      departmentId?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.search) searchParams.set('search', params.search);
      if (params?.status) searchParams.set('status', params.status);
      if (params?.departmentId) searchParams.set('departmentId', params.departmentId);
      const query = searchParams.toString();
      return fetchApi(`/job-postings${query ? `?${query}` : ''}`);
    },

    getJobPosting: (id: string) =>
      fetchApi(`/job-postings/${id}`),

    createJobPosting: (data: Record<string, unknown>) =>
      fetchApi('/job-postings', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateJobPosting: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/job-postings/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deleteJobPosting: (id: string) =>
      fetchApi(`/job-postings/${id}`, {
        method: 'DELETE',
      }),

    publishJobPosting: (id: string) =>
      fetchApi(`/job-postings/${id}/publish`, {
        method: 'POST',
      }),

    closeJobPosting: (id: string) =>
      fetchApi(`/job-postings/${id}/close`, {
        method: 'POST',
      }),

    // Candidates
    listCandidates: (params?: {
      page?: number;
      pageSize?: number;
      search?: string;
      stage?: string;
      jobPostingId?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.search) searchParams.set('search', params.search);
      if (params?.stage) searchParams.set('stage', params.stage);
      if (params?.jobPostingId) searchParams.set('jobPostingId', params.jobPostingId);
      const query = searchParams.toString();
      return fetchApi(`/candidates${query ? `?${query}` : ''}`);
    },

    getCandidate: (id: string) =>
      fetchApi(`/candidates/${id}`),

    createCandidate: (data: Record<string, unknown>) =>
      fetchApi('/candidates', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateCandidate: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/candidates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    updateCandidateStage: (id: string, stage: string) =>
      fetchApi(`/candidates/${id}/stage`, {
        method: 'PATCH',
        body: JSON.stringify({ stage }),
      }),

    deleteCandidate: (id: string) =>
      fetchApi(`/candidates/${id}`, {
        method: 'DELETE',
      }),

    getPipelineStats: (jobPostingId?: string) => {
      const query = jobPostingId ? `?jobPostingId=${jobPostingId}` : '';
      return fetchApi(`/candidates/pipeline-stats${query}`);
    },
  },

  sso: {
    // Public endpoint - get available SSO providers for a tenant
    getProviders: (tenantSlug: string): Promise<SsoProviderInfo[]> =>
      fetchApi(`/auth/sso/providers?tenant=${tenantSlug}`),

    // Admin endpoints - SSO configuration management
    getConfigs: (): Promise<SsoConfig[]> =>
      fetchApi('/auth/sso/config'),

    getConfig: (id: string): Promise<SsoConfig> =>
      fetchApi(`/auth/sso/config/${id}`),

    createConfig: (data: CreateSsoConfigInput): Promise<SsoConfig> =>
      fetchApi('/auth/sso/config', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateConfig: (id: string, data: UpdateSsoConfigInput): Promise<SsoConfig> =>
      fetchApi(`/auth/sso/config/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deleteConfig: (id: string): Promise<void> =>
      fetchApi(`/auth/sso/config/${id}`, {
        method: 'DELETE',
      }),

    testConnection: (id: string): Promise<SsoTestResult> =>
      fetchApi(`/auth/sso/config/${id}/test`, {
        method: 'POST',
      }),

    getMetadata: (configId: string): Promise<SsoMetadata> =>
      fetchApi(`/auth/sso/${configId}/metadata`),

    // Get login URL for SSO provider
    getLoginUrl: (configId: string, redirectUrl?: string): string => {
      const params = redirectUrl ? `?redirect=${encodeURIComponent(redirectUrl)}` : '';
      return `/api/auth/sso/${configId}/login${params}`;
    },
  },

  // Dashboard API
  dashboard: {
    getMetrics: () => fetchApi<DashboardMetrics>('/dashboard/metrics'),
    getHeadcountTrend: () => fetchApi<HeadcountTrendData[]>('/dashboard/headcount-trend'),
    getDepartmentBreakdown: () => fetchApi<DepartmentBreakdown[]>('/dashboard/department-breakdown'),
    getDiversity: () => fetchApi<DiversityMetrics>('/dashboard/diversity'),
  },

  // Self-Service API
  selfService: {
    getMyProfile: () => fetchApi('/self-service/profile'),
    updateMyPersonalInfo: (data: Record<string, unknown>) =>
      fetchApi('/self-service/personal-info', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    getEmergencyContacts: () => fetchApi('/self-service/emergency-contacts'),
    addEmergencyContact: (data: Record<string, unknown>) =>
      fetchApi('/self-service/emergency-contacts', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateEmergencyContact: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/self-service/emergency-contacts/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    deleteEmergencyContact: (id: string) =>
      fetchApi(`/self-service/emergency-contacts/${id}`, {
        method: 'DELETE',
      }),
    getDirectDepositAccounts: () => fetchApi('/self-service/direct-deposit'),
    addDirectDepositAccount: (data: Record<string, unknown>) =>
      fetchApi('/self-service/direct-deposit', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    updateDirectDepositAccount: (id: string, data: Record<string, unknown>) =>
      fetchApi(`/self-service/direct-deposit/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    deleteDirectDepositAccount: (id: string) =>
      fetchApi(`/self-service/direct-deposit/${id}`, {
        method: 'DELETE',
      }),
    getMyCompensationHistory: () => fetchApi('/self-service/compensation-history'),
    getDirectReports: () => fetchApi('/self-service/direct-reports'),
    getTeamOverview: () => fetchApi('/self-service/team-overview'),
    getTeamPendingLeaveRequests: () => fetchApi('/self-service/team/leave-requests'),
    getTeamTimeOffCalendar: (startDate: string, endDate: string) =>
      fetchApi(`/self-service/team/time-off-calendar?startDate=${startDate}&endDate=${endDate}`),
    getTeamMemberProfile: (employeeId: string) =>
      fetchApi(`/self-service/team/${employeeId}/profile`),
    getTeamMemberPerformanceHistory: (employeeId: string) =>
      fetchApi(`/self-service/team/${employeeId}/performance-history`),
  },

  companyDocuments: {
    list: (params?: {
      page?: number;
      pageSize?: number;
      category?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.category) searchParams.set('category', params.category);

      const query = searchParams.toString();
      return fetchApi(`/company-documents${query ? `?${query}` : ''}`);
    },

    get: (id: string) => fetchApi(`/company-documents/${id}`),

    getPending: () => fetchApi('/company-documents/pending'),

    getStats: () => fetchApi('/company-documents/stats'),

    create: (formData: FormData) =>
      fetchApi('/company-documents', {
        method: 'POST',
        body: formData,
        headers: {}, // Let browser set Content-Type with boundary for multipart
      }),

    update: (id: string, data: {
      title?: string;
      description?: string;
      category?: string;
      requiresAcknowledgment?: boolean;
      isActive?: boolean;
    }) =>
      fetchApi(`/company-documents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchApi(`/company-documents/${id}`, {
        method: 'DELETE',
      }),

    uploadVersion: (documentId: string, formData: FormData) =>
      fetchApi(`/company-documents/${documentId}/versions`, {
        method: 'POST',
        body: formData,
        headers: {}, // Let browser set Content-Type with boundary for multipart
      }),

    getDownloadUrl: (id: string, versionId?: string) => {
      const query = versionId ? `?versionId=${versionId}` : '';
      return fetchApi(`/company-documents/${id}/download${query}`);
    },

    getViewUrl: (id: string, versionId?: string) => {
      const query = versionId ? `?versionId=${versionId}` : '';
      return fetchApi(`/company-documents/${id}/view${query}`);
    },

    getStreamUrl: (id: string, versionId?: string) => {
      // Return a URL that can be used directly in iframes/images
      // Uses dedicated streaming route that handles binary data and cookie auth
      const query = versionId ? `?versionId=${versionId}` : '';
      return `/api/documents/${id}/stream${query}`;
    },

    acknowledge: (documentId: string, data: {
      signatureType: 'CHECKBOX' | 'TYPED_NAME' | 'DRAWN';
      signatureData?: {
        typedName?: string;
        drawingData?: string;
      };
    }) =>
      fetchApi(`/company-documents/${documentId}/acknowledge`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    getAcknowledgmentStatus: (documentId: string, params?: {
      page?: number;
      pageSize?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

      const query = searchParams.toString();
      return fetchApi(`/company-documents/${documentId}/acknowledgments${query ? `?${query}` : ''}`);
    },

    // Document Assignment APIs
    assignDocument: (documentId: string, data: {
      assignmentType: 'ALL_EMPLOYEES' | 'DEPARTMENT' | 'EMPLOYEE';
      departmentIds?: string[];
      employeeIds?: string[];
      dueDate?: string;
      sendNotification?: boolean;
    }) =>
      fetchApi(`/company-documents/${documentId}/assign`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    getAssignments: (documentId: string) =>
      fetchApi(`/company-documents/${documentId}/assignments`),

    getAssignmentStatus: (documentId: string, params?: {
      page?: number;
      pageSize?: number;
      status?: 'pending' | 'acknowledged' | 'all';
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.set('status', params.status);

      const query = searchParams.toString();
      return fetchApi(`/company-documents/${documentId}/assignment-status${query ? `?${query}` : ''}`);
    },

    // Reminder Schedule APIs
    createReminderSchedule: (documentId: string, data: {
      reminderType: 'DAYS_BEFORE_DUE' | 'DAYS_AFTER_ASSIGNED' | 'SPECIFIC_DATE';
      daysOffset?: number;
      specificDate?: string;
    }) =>
      fetchApi(`/company-documents/${documentId}/reminders`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    getReminderSchedules: (documentId: string) =>
      fetchApi(`/company-documents/${documentId}/reminders`),

    deleteReminderSchedule: (documentId: string, scheduleId: string) =>
      fetchApi(`/company-documents/${documentId}/reminders/${scheduleId}`, {
        method: 'DELETE',
      }),

    sendReminders: (documentId: string, data?: {
      employeeIds?: string[];
      customMessage?: string;
    }) =>
      fetchApi(`/company-documents/${documentId}/send-reminders`, {
        method: 'POST',
        body: JSON.stringify(data || {}),
      }),

    // Notification History APIs
    getNotificationHistory: (documentId: string, params?: {
      page?: number;
      pageSize?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

      const query = searchParams.toString();
      return fetchApi(`/company-documents/${documentId}/notifications${query ? `?${query}` : ''}`);
    },
  },

  // Learning - Courses
  courses: {
    list: (params?: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
      category?: string;
      format?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.search) searchParams.set('search', params.search);
      if (params?.status) searchParams.set('status', params.status);
      if (params?.category) searchParams.set('category', params.category);
      if (params?.format) searchParams.set('format', params.format);
      if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
      if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);

      const query = searchParams.toString();
      return fetchApi<{
        data: Course[];
        meta: { pagination: { page: number; pageSize: number; total: number; totalPages: number } };
      }>(`/learning/courses${query ? `?${query}` : ''}`);
    },

    get: (id: string) => fetchApi<Course>(`/learning/courses/${id}`),

    create: (data: CreateCourseInput) =>
      fetchApi<Course>('/learning/courses', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: UpdateCourseInput) =>
      fetchApi<Course>(`/learning/courses/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchApi<void>(`/learning/courses/${id}`, {
        method: 'DELETE',
      }),

    getCategories: () =>
      fetchApi<{ categories: string[] }>('/learning/courses/categories'),

    // Lessons
    getLessons: (courseId: string) =>
      fetchApi<{ data: Lesson[] }>(`/learning/courses/${courseId}/lessons`),

    createLesson: (data: CreateLessonInput) =>
      fetchApi<Lesson>('/learning/lessons', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateLesson: (id: string, data: UpdateLessonInput) =>
      fetchApi<Lesson>(`/learning/lessons/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    deleteLesson: (id: string) =>
      fetchApi<void>(`/learning/lessons/${id}`, {
        method: 'DELETE',
      }),
  },

  succession: {
    // Succession Plans
    createPlan: (data: {
      positionId: string;
      incumbentId?: string;
      name: string;
      description?: string;
      incumbentFlightRisk?: string;
      positionCriticality?: string;
      targetDate?: string;
    }) =>
      fetchApi('/succession/plans', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    listPlans: (params?: {
      status?: string;
      departmentId?: string;
      criticality?: string;
      includeEmpty?: boolean;
      page?: number;
      pageSize?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.set('status', params.status);
      if (params?.departmentId) searchParams.set('departmentId', params.departmentId);
      if (params?.criticality) searchParams.set('criticality', params.criticality);
      if (params?.includeEmpty) searchParams.set('includeEmpty', String(params.includeEmpty));
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/succession/plans${query ? `?${query}` : ''}`);
    },

    getPlan: (planId: string) =>
      fetchApi(`/succession/plans/${planId}`),

    addSuccessor: (data: {
      planId: string;
      employeeId: string;
      readinessLevel: string;
      ranking?: number;
      notes?: string;
      developmentAreas?: string[];
    }) =>
      fetchApi('/succession/plans/successors', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateSuccessorReadiness: (successorId: string, data: {
      readinessLevel: string;
      assessmentNotes?: string;
      achievements?: string[];
      developmentGaps?: string[];
    }) =>
      fetchApi(`/succession/successors/${successorId}/readiness`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    removeSuccessor: (successorId: string) =>
      fetchApi(`/succession/successors/${successorId}`, {
        method: 'DELETE',
      }),

    // Talent Pools
    createTalentPool: (data: {
      name: string;
      description?: string;
      type: string;
      criteria?: string;
    }) =>
      fetchApi('/succession/talent-pools', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    listTalentPools: () =>
      fetchApi('/succession/talent-pools'),

    addToTalentPool: (data: {
      poolId: string;
      employeeId: string;
      notes?: string;
      nominatedBy?: string;
    }) =>
      fetchApi('/succession/talent-pools/members', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    removeFromTalentPool: (poolId: string, employeeId: string) =>
      fetchApi(`/succession/talent-pools/${poolId}/members/${employeeId}`, {
        method: 'DELETE',
      }),

    // 9-Box Grid
    update9BoxPosition: (data: {
      employeeId: string;
      performance: string;
      potential: string;
      notes?: string;
      reviewPeriod?: string;
    }) =>
      fetchApi('/succession/9-box', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    get9BoxGrid: (params?: {
      departmentId?: string;
      managerId?: string;
      reviewPeriod?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.departmentId) searchParams.set('departmentId', params.departmentId);
      if (params?.managerId) searchParams.set('managerId', params.managerId);
      if (params?.reviewPeriod) searchParams.set('reviewPeriod', params.reviewPeriod);
      const query = searchParams.toString();
      return fetchApi(`/succession/9-box${query ? `?${query}` : ''}`);
    },

    // Reports
    getRiskReport: () =>
      fetchApi('/succession/risk-report'),

    // Development Plans
    createDevelopmentPlan: (data: {
      employeeId: string;
      targetPositionId?: string;
      title: string;
      description?: string;
      targetDate?: string;
      activities?: Array<{
        title: string;
        description?: string;
        type: string;
        dueDate?: string;
        completed?: boolean;
      }>;
    }) =>
      fetchApi('/succession/development-plans', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    getEmployeeDevelopmentPlans: (employeeId: string) =>
      fetchApi(`/succession/development-plans/employee/${employeeId}`),
  },

  analytics: {
    // Turnover Risk
    getTurnoverRiskList: (params?: {
      minRiskLevel?: string;
      departmentId?: string;
      managerId?: string;
      minRiskScore?: number;
      page?: number;
      pageSize?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.minRiskLevel) searchParams.set('minRiskLevel', params.minRiskLevel);
      if (params?.departmentId) searchParams.set('departmentId', params.departmentId);
      if (params?.managerId) searchParams.set('managerId', params.managerId);
      if (params?.minRiskScore) searchParams.set('minRiskScore', params.minRiskScore.toString());
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/analytics/turnover-risk${query ? `?${query}` : ''}`);
    },

    getTurnoverRiskSummary: () =>
      fetchApi('/analytics/turnover-risk/summary'),

    getEmployeeTurnoverRisk: (employeeId: string) =>
      fetchApi(`/analytics/turnover-risk/employee/${employeeId}`),

    // Workforce Forecasting
    generateForecast: (data: {
      type: string;
      startDate: string;
      endDate: string;
      granularity: string;
      departmentId?: string;
      locationId?: string;
      includeSeasonality?: boolean;
      includePlannedHiring?: boolean;
      includePredictedAttrition?: boolean;
    }) =>
      fetchApi('/analytics/forecast', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    runWorkforcePlanScenario: (data: {
      name: string;
      description?: string;
      growthRate: number;
      attritionRate: number;
      plannedHires?: number;
      budgetConstraint?: number;
    }) =>
      fetchApi('/analytics/workforce-plan/scenario', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // HR Metrics
    getMetrics: (data: {
      metrics: string[];
      startDate: string;
      endDate: string;
      granularity?: string;
      departmentId?: string;
      locationId?: string;
      compareToPrevious?: boolean;
    }) =>
      fetchApi('/analytics/metrics', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Skills Gap Analysis
    getSkillGapAnalysis: (departmentId?: string) => {
      const query = departmentId ? `?departmentId=${departmentId}` : '';
      return fetchApi(`/analytics/skills-gap${query}`);
    },

    // Compensation Analytics
    getCompensationAnalytics: (departmentId?: string) => {
      const query = departmentId ? `?departmentId=${departmentId}` : '';
      return fetchApi(`/analytics/compensation${query}`);
    },

    // Diversity Metrics
    getDiversityMetrics: () =>
      fetchApi('/analytics/diversity'),

    // Benchmarking
    getBenchmarkReport: () =>
      fetchApi('/analytics/benchmark'),
  },

  backgroundChecks: {
    // Provider Configuration
    configureProvider: (data: {
      provider: string;
      apiKey: string;
      apiSecret?: string;
      webhookUrl?: string;
      sandbox?: boolean;
    }) =>
      fetchApi('/background-checks/config/providers', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    getProviderConfigs: () =>
      fetchApi('/background-checks/config/providers'),

    // Packages
    createPackage: (data: {
      name: string;
      description?: string;
      type: string;
      checks: string[];
      estimatedTurnaround?: number;
    }) =>
      fetchApi('/background-checks/packages', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    getPackages: () =>
      fetchApi('/background-checks/packages'),

    getPackage: (packageId: string) =>
      fetchApi(`/background-checks/packages/${packageId}`),

    updatePackage: (packageId: string, data: Record<string, unknown>) =>
      fetchApi(`/background-checks/packages/${packageId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    deactivatePackage: (packageId: string) =>
      fetchApi(`/background-checks/packages/${packageId}`, {
        method: 'DELETE',
      }),

    // Background Checks
    initiateCheck: (data: {
      candidateId: string;
      packageId: string;
      provider?: string;
      notes?: string;
    }) =>
      fetchApi('/background-checks', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    listChecks: (params?: {
      status?: string;
      provider?: string;
      result?: string;
      page?: number;
      pageSize?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.set('status', params.status);
      if (params?.provider) searchParams.set('provider', params.provider);
      if (params?.result) searchParams.set('result', params.result);
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/background-checks${query ? `?${query}` : ''}`);
    },

    getCheck: (checkId: string) =>
      fetchApi(`/background-checks/${checkId}`),

    getChecksForCandidate: (candidateId: string) =>
      fetchApi(`/background-checks/candidate/${candidateId}`),

    getSummary: () =>
      fetchApi('/background-checks/summary'),

    cancelCheck: (checkId: string) =>
      fetchApi(`/background-checks/${checkId}/cancel`, {
        method: 'POST',
      }),

    recordConsent: (checkId: string, data: {
      signature: string;
      ipAddress: string;
      userAgent?: string;
    }) =>
      fetchApi(`/background-checks/${checkId}/consent`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  eSignature: {
    // Provider Configuration
    configureProvider: (data: {
      provider: string;
      apiKey: string;
      apiSecret?: string;
      sandbox?: boolean;
    }) =>
      fetchApi('/e-signature/config/providers', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    getProviderConfigs: () =>
      fetchApi('/e-signature/config/providers'),

    // Templates
    createTemplate: (data: {
      name: string;
      description?: string;
      documentType: string;
      documentUrl?: string;
      signerRoles: Array<{ name: string; order: number }>;
    }) =>
      fetchApi('/e-signature/templates', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    getTemplates: (documentType?: string) => {
      const query = documentType ? `?documentType=${documentType}` : '';
      return fetchApi(`/e-signature/templates${query}`);
    },

    getTemplate: (templateId: string) =>
      fetchApi(`/e-signature/templates/${templateId}`),

    deactivateTemplate: (templateId: string) =>
      fetchApi(`/e-signature/templates/${templateId}`, {
        method: 'DELETE',
      }),

    // Envelopes
    createEnvelope: (data: {
      templateId: string;
      subject: string;
      message?: string;
      recipients: Array<{
        role: string;
        name: string;
        email: string;
        employeeId?: string;
        candidateId?: string;
      }>;
      entityType?: string;
      entityId?: string;
    }) =>
      fetchApi('/e-signature/envelopes', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    listEnvelopes: (params?: {
      status?: string;
      documentType?: string;
      entityType?: string;
      entityId?: string;
      page?: number;
      pageSize?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.set('status', params.status);
      if (params?.documentType) searchParams.set('documentType', params.documentType);
      if (params?.entityType) searchParams.set('entityType', params.entityType);
      if (params?.entityId) searchParams.set('entityId', params.entityId);
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/e-signature/envelopes${query ? `?${query}` : ''}`);
    },

    getEnvelope: (envelopeId: string) =>
      fetchApi(`/e-signature/envelopes/${envelopeId}`),

    getSummary: () =>
      fetchApi('/e-signature/envelopes/summary'),

    sendEnvelope: (envelopeId: string) =>
      fetchApi(`/e-signature/envelopes/${envelopeId}/send`, {
        method: 'POST',
      }),

    voidEnvelope: (envelopeId: string, reason: string) =>
      fetchApi(`/e-signature/envelopes/${envelopeId}/void`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),

    resendToRecipient: (envelopeId: string, recipientId: string) =>
      fetchApi(`/e-signature/envelopes/${envelopeId}/resend/${recipientId}`, {
        method: 'POST',
      }),
  },

  i9EVerify: {
    listI9Forms: (params?: {
      status?: string;
      section1Complete?: boolean;
      section2Complete?: boolean;
      page?: number;
      pageSize?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.set('status', params.status);
      if (params?.section1Complete !== undefined) searchParams.set('section1Complete', String(params.section1Complete));
      if (params?.section2Complete !== undefined) searchParams.set('section2Complete', String(params.section2Complete));
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/i9-everify/i9${query ? `?${query}` : ''}`);
    },

    getI9: (i9Id: string) =>
      fetchApi(`/i9-everify/i9/${i9Id}`),

    getI9ByEmployee: (employeeId: string) =>
      fetchApi(`/i9-everify/i9/employee/${employeeId}`),

    getSummary: () =>
      fetchApi('/i9-everify/i9/summary'),

    getComplianceReport: () =>
      fetchApi('/i9-everify/compliance/report'),

    submitToEVerify: (data: { i9Id: string }) =>
      fetchApi('/i9-everify/everify/submit', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    getEVerifyCase: (caseId: string) =>
      fetchApi(`/i9-everify/everify/case/${caseId}`),

    listEVerifyCases: (params?: {
      status?: string;
      page?: number;
      pageSize?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.set('status', params.status);
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/i9-everify/everify/cases${query ? `?${query}` : ''}`);
    },
  },

  globalPayroll: {
    // Country Configuration
    getCountryConfigs: () =>
      fetchApi('/global-payroll/countries'),

    enableCountry: (config: {
      country: string;
      currency: string;
      payFrequency: string;
      taxSettings?: Record<string, unknown>;
    }) =>
      fetchApi('/global-payroll/countries', {
        method: 'POST',
        body: JSON.stringify(config),
      }),

    disableCountry: (country: string) =>
      fetchApi(`/global-payroll/countries/${country}/disable`, {
        method: 'PUT',
      }),

    // Exchange Rates
    getExchangeRates: () =>
      fetchApi('/global-payroll/exchange-rates'),

    setExchangeRate: (data: {
      fromCurrency: string;
      toCurrency: string;
      rate: number;
      effectiveDate: string;
    }) =>
      fetchApi('/global-payroll/exchange-rates', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    convertCurrency: (data: {
      amount: number;
      fromCurrency: string;
      toCurrency: string;
    }) =>
      fetchApi('/global-payroll/convert', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Employee Settings
    getEmployeePayrollSettings: (employeeId: string) =>
      fetchApi(`/global-payroll/employees/${employeeId}/settings`),

    updateEmployeePayrollSettings: (data: {
      employeeId: string;
      country: string;
      currency: string;
      bankDetails?: Record<string, unknown>;
      taxId?: string;
    }) =>
      fetchApi('/global-payroll/employees/settings', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // Payroll Runs
    listPayrollRuns: (params?: {
      country?: string;
      status?: string;
      startDate?: string;
      endDate?: string;
      page?: number;
      pageSize?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.country) searchParams.set('country', params.country);
      if (params?.status) searchParams.set('status', params.status);
      if (params?.startDate) searchParams.set('startDate', params.startDate);
      if (params?.endDate) searchParams.set('endDate', params.endDate);
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/global-payroll/runs${query ? `?${query}` : ''}`);
    },

    getPayrollRun: (runId: string) =>
      fetchApi(`/global-payroll/runs/${runId}`),

    createPayrollRun: (data: {
      country: string;
      payPeriodStart: string;
      payPeriodEnd: string;
      payDate: string;
      description?: string;
    }) =>
      fetchApi('/global-payroll/runs', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    submitForApproval: (runId: string) =>
      fetchApi(`/global-payroll/runs/${runId}/submit`, {
        method: 'POST',
      }),

    approvePayrollRun: (runId: string) =>
      fetchApi(`/global-payroll/runs/${runId}/approve`, {
        method: 'POST',
      }),

    processPayrollRun: (runId: string) =>
      fetchApi(`/global-payroll/runs/${runId}/process`, {
        method: 'POST',
      }),

    // Summary & Reports
    getSummary: () =>
      fetchApi('/global-payroll/summary'),

    getComplianceReport: () =>
      fetchApi('/global-payroll/compliance'),
  },

  auditLogs: {
    list: (params?: {
      action?: string;
      entityType?: string;
      entityId?: string;
      userId?: string;
      startDate?: string;
      endDate?: string;
      page?: number;
      pageSize?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.action) searchParams.set('action', params.action);
      if (params?.entityType) searchParams.set('entityType', params.entityType);
      if (params?.entityId) searchParams.set('entityId', params.entityId);
      if (params?.userId) searchParams.set('userId', params.userId);
      if (params?.startDate) searchParams.set('startDate', params.startDate);
      if (params?.endDate) searchParams.set('endDate', params.endDate);
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/audit-logs${query ? `?${query}` : ''}`);
    },

    get: (id: string) =>
      fetchApi(`/audit-logs/${id}`),

    getSummary: () =>
      fetchApi('/audit-logs/summary'),
  },

  // Admin Panel (SUPER_ADMIN)
  admin: {
    stats: () => fetchApi('/admin/stats'),

    auditLogs: (params?: {
      page?: number;
      pageSize?: number;
      action?: string;
      entityType?: string;
      userId?: string;
      startDate?: string;
      endDate?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.action) searchParams.set('action', params.action);
      if (params?.entityType) searchParams.set('entityType', params.entityType);
      if (params?.userId) searchParams.set('userId', params.userId);
      if (params?.startDate) searchParams.set('startDate', params.startDate);
      if (params?.endDate) searchParams.set('endDate', params.endDate);
      const query = searchParams.toString();
      return fetchApi(`/admin/audit-logs${query ? `?${query}` : ''}`);
    },

    users: (params?: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
      tenantId?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.search) searchParams.set('search', params.search);
      if (params?.status) searchParams.set('status', params.status);
      if (params?.tenantId) searchParams.set('tenantId', params.tenantId);
      const query = searchParams.toString();
      return fetchApi(`/admin/users${query ? `?${query}` : ''}`);
    },

    tenants: (params?: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
      plan?: string;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.search) searchParams.set('search', params.search);
      if (params?.status) searchParams.set('status', params.status);
      if (params?.plan) searchParams.set('plan', params.plan);
      const query = searchParams.toString();
      return fetchApi(`/admin/tenants${query ? `?${query}` : ''}`);
    },

    getTenant: (slug: string) => fetchApi(`/admin/tenants/${slug}`),

    // User detail & management
    getUser: (id: string) => fetchApi(`/admin/users/${id}`),

    updateUserStatus: (id: string, status: string) =>
      fetchApi(`/admin/users/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),

    setUserPlatformAdmin: (id: string, isPlatformAdmin: boolean) =>
      fetchApi(`/admin/users/${id}/platform-admin`, {
        method: 'PATCH',
        body: JSON.stringify({ isPlatformAdmin }),
      }),

    resetUserPassword: (id: string) =>
      fetchApi(`/admin/users/${id}/reset-password`, {
        method: 'POST',
      }),

    // Impersonation
    createImpersonationRequest: (data: {
      targetUserId: string;
      tenantId: string;
      reason: string;
    }) =>
      fetchApi('/admin/impersonation-requests', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    listImpersonationRequests: (params?: {
      status?: string;
      page?: number;
      pageSize?: number;
    }) => {
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.set('status', params.status);
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/admin/impersonation-requests${query ? `?${query}` : ''}`);
    },

    getPendingImpersonationRequests: () =>
      fetchApi('/admin/impersonation-requests/pending'),

    approveImpersonationRequest: (id: string) =>
      fetchApi(`/admin/impersonation-requests/${id}/approve`, {
        method: 'POST',
      }),

    denyImpersonationRequest: (id: string, reason?: string) =>
      fetchApi(`/admin/impersonation-requests/${id}/deny`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),

    activateImpersonation: (id: string) =>
      fetchApi(`/admin/impersonation-requests/${id}/activate`, {
        method: 'POST',
      }),

    endImpersonation: (id: string) =>
      fetchApi(`/admin/impersonation-requests/${id}/end`, {
        method: 'POST',
      }),
  },

  // Billing & Subscriptions
  billing: {
    getPricing: (): Promise<Array<{ plan: string; name: string; pricePerEmployee: number | null; features: string[] }>> =>
      fetchApi('/billing/pricing'),

    getSubscription: (): Promise<{
      id: string;
      status: string;
      plan: string;
      employeeCount: number;
      stripeCustomerId?: string;
      stripeSubscriptionId?: string;
      currentPeriodStart?: string;
      currentPeriodEnd?: string;
      cancelAtPeriodEnd: boolean;
      createdAt: string;
    }> => fetchApi('/billing/subscription'),

    createCheckout: (data: { plan: 'STARTER' | 'PROFESSIONAL'; successUrl?: string; cancelUrl?: string }): Promise<{ url: string }> =>
      fetchApi('/billing/checkout', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    createPortal: (data?: { returnUrl?: string }): Promise<{ url: string }> =>
      fetchApi('/billing/portal', {
        method: 'POST',
        body: JSON.stringify(data || {}),
      }),

    changePlan: (plan: 'STARTER' | 'PROFESSIONAL'): Promise<{ success: boolean }> =>
      fetchApi('/billing/subscription/plan', {
        method: 'PATCH',
        body: JSON.stringify({ plan }),
      }),

    cancelSubscription: (): Promise<{ success: boolean }> =>
      fetchApi('/billing/subscription', { method: 'DELETE' }),

    resumeSubscription: (): Promise<{ success: boolean }> =>
      fetchApi('/billing/subscription/resume', { method: 'POST' }),

    syncEmployees: (): Promise<{ employeeCount: number }> =>
      fetchApi('/billing/sync-employees', { method: 'POST' }),
  },

  // Employee Events
  employeeEvents: {
    listByEmployee: (employeeId: string, params?: { page?: number; pageSize?: number; eventType?: string }) => {
      const searchParams = new URLSearchParams();
      searchParams.set('employeeId', employeeId);
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.eventType) searchParams.set('eventType', params.eventType);
      const query = searchParams.toString();
      return fetchApi(`/employee-events${query ? `?${query}` : ''}`);
    },
  },

  // Employee Notes
  employeeNotes: {
    list: (employeeId: string, params?: { page?: number; pageSize?: number; category?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.category) searchParams.set('category', params.category);
      const query = searchParams.toString();
      return fetchApi(`/employees/${employeeId}/notes${query ? `?${query}` : ''}`);
    },

    create: (employeeId: string, data: {
      content: string;
      visibility?: string;
      category?: string;
      isPinned?: boolean;
    }) =>
      fetchApi(`/employees/${employeeId}/notes`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (employeeId: string, noteId: string, data: {
      content?: string;
      visibility?: string;
      category?: string;
      isPinned?: boolean;
    }) =>
      fetchApi(`/employees/${employeeId}/notes/${noteId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (employeeId: string, noteId: string) =>
      fetchApi(`/employees/${employeeId}/notes/${noteId}`, {
        method: 'DELETE',
      }),
  },

  // Identity Map
  identityMap: {
    list: (employeeId: string) =>
      fetchApi(`/employees/${employeeId}/identities`),

    connect: (employeeId: string, data: {
      provider: string;
      externalId: string;
      externalEmail?: string;
      externalUsername?: string;
      displayName?: string;
      profileUrl?: string;
      metadata?: Record<string, unknown>;
    }) =>
      fetchApi(`/employees/${employeeId}/identities`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    disconnect: (employeeId: string, identityId: string) =>
      fetchApi(`/employees/${employeeId}/identities/${identityId}`, {
        method: 'DELETE',
      }),
  },

  // Workflows
  workflows: {
    list: (params?: { page?: number; pageSize?: number; isActive?: boolean; triggerEventType?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString());
      if (params?.triggerEventType) searchParams.set('triggerEventType', params.triggerEventType);
      const query = searchParams.toString();
      return fetchApi(`/workflows${query ? `?${query}` : ''}`);
    },

    get: (id: string) =>
      fetchApi(`/workflows/${id}`),

    create: (data: {
      name: string;
      description?: string;
      triggerEventType: string;
      triggerConditions?: Record<string, unknown>;
      actions: Array<{ type: string; config: Record<string, unknown> }>;
      requiresApproval?: boolean;
      isActive?: boolean;
    }) =>
      fetchApi('/workflows', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: {
      name?: string;
      description?: string;
      triggerEventType?: string;
      triggerConditions?: Record<string, unknown>;
      actions?: Array<{ type: string; config: Record<string, unknown> }>;
      requiresApproval?: boolean;
      isActive?: boolean;
    }) =>
      fetchApi(`/workflows/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchApi(`/workflows/${id}`, {
        method: 'DELETE',
      }),

    executions: (workflowId: string, params?: { page?: number; pageSize?: number; status?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      if (params?.status) searchParams.set('status', params.status);
      const query = searchParams.toString();
      return fetchApi(`/workflows/${workflowId}/executions${query ? `?${query}` : ''}`);
    },
  },

  // Org Chart Snapshots
  orgChartSnapshots: {
    list: (params?: { page?: number; pageSize?: number }) => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
      const query = searchParams.toString();
      return fetchApi(`/employees/org-chart/snapshots${query ? `?${query}` : ''}`);
    },

    create: (data: { label?: string; snapshotDate?: string }) =>
      fetchApi('/employees/org-chart/snapshot', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  // Waitlist
  waitlist: {
    list: () =>
      fetchApi<Array<{
        id: string;
        email: string;
        name: string | null;
        source: string;
        createdAt: string;
      }>>('/waitlist'),
  },

  // Convenience methods for generic API calls
  get: <T>(endpoint: string) => fetchApi<T>(endpoint),
  post: <T>(endpoint: string, data?: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),
  patch: <T>(endpoint: string, data?: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),
  put: <T>(endpoint: string, data?: unknown) =>
    fetchApi<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),
  delete: <T>(endpoint: string) =>
    fetchApi<T>(endpoint, {
      method: 'DELETE',
    }),

  // Export fetchApi for custom usage
  fetchApi,
};

// Dashboard types
export interface DashboardMetrics {
  totalHeadcount: number;
  activeEmployees: number;
  terminatedThisMonth: number;
  newHiresThisMonth: number;
  openPositions: number;
  turnoverRate: number;
  avgTenure: number;
  departmentCount: number;
  // Additional properties used in components
  newHires: number;
  terminations: number;
  pendingApprovals: number;
  pendingLeaveRequests: number;
  // Executive dashboard metrics
  averageTimeToFill?: number;
  averageTimeToHire?: number;
  offerAcceptanceRate?: number;
  costPerHire?: number;
  upcomingReviews?: number;
}

export interface HeadcountTrendData {
  month: string;
  totalHeadcount: number;
  byDepartment?: Array<{
    departmentId: string;
    departmentName: string;
    count: number;
  }>;
}

export interface DepartmentBreakdown {
  departmentId: string;
  departmentName: string;
  employeeCount: number;
  budgetHeadcount: number | null;
  variance: number | null;
  percentOfTotal: number;
}

export interface DiversityMetrics {
  genderDistribution: Array<{
    gender: string;
    count: number;
    percentage: number;
  }>;
  ethnicityDistribution: Record<string, number>;
  ageDistribution: Array<{
    ageGroup: string;
    count: number;
    percentage: number;
  }>;
  tenureDistribution: Array<{
    tenureGroup: string;
    count: number;
    percentage: number;
  }>;
}

// SSO Types
export interface SsoProviderInfo {
  id: string;
  provider: 'SAML' | 'OIDC' | 'OKTA' | 'AZURE_AD' | 'GOOGLE_WORKSPACE';
  name: string;
  enabled: boolean;
  loginUrl: string;
}

export interface SsoConfig {
  id: string;
  tenantId: string;
  provider: 'SAML' | 'OIDC' | 'OKTA' | 'AZURE_AD' | 'GOOGLE_WORKSPACE';
  name: string;
  enabled: boolean;
  attributeMapping?: Record<string, string>;
  allowIdpInitiated: boolean;
  forceAuthn: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSsoConfigInput {
  provider: 'SAML' | 'OIDC' | 'OKTA' | 'AZURE_AD' | 'GOOGLE_WORKSPACE';
  name: string;
  enabled?: boolean;
  samlConfig?: {
    entityId: string;
    ssoUrl: string;
    sloUrl?: string;
    certificate: string;
    metadata?: string;
  };
  oidcConfig?: {
    clientId: string;
    clientSecret: string;
    issuer: string;
    authorizationUrl?: string;
    tokenUrl?: string;
    userinfoUrl?: string;
    jwksUri?: string;
    scopes?: string[];
  };
  attributeMapping?: Record<string, string>;
  allowIdpInitiated?: boolean;
  forceAuthn?: boolean;
}

export interface UpdateSsoConfigInput {
  name?: string;
  enabled?: boolean;
  samlConfig?: {
    entityId?: string;
    ssoUrl?: string;
    sloUrl?: string;
    certificate?: string;
    metadata?: string;
  };
  oidcConfig?: {
    clientId?: string;
    clientSecret?: string;
    issuer?: string;
    authorizationUrl?: string;
    tokenUrl?: string;
    userinfoUrl?: string;
    jwksUri?: string;
    scopes?: string[];
  };
  attributeMapping?: Record<string, string>;
  allowIdpInitiated?: boolean;
  forceAuthn?: boolean;
}

export interface SsoTestResult {
  success: boolean;
  error?: string;
  details?: Record<string, unknown>;
}

export interface SsoMetadata {
  entityId: string;
  acsUrl: string;
  sloUrl: string;
  metadataXml: string;
}

// Feature Flags Types
export interface FeatureFlag {
  id: string;
  key: string;
  name: string;
  description?: string;
  type: 'BOOLEAN' | 'STRING' | 'NUMBER' | 'JSON';
  defaultValue: unknown;
  status: 'ACTIVE' | 'INACTIVE';
  category?: string;
  createdAt: string;
  updatedAt: string;
  overrides?: FeatureFlagOverride[];
}

export interface FeatureFlagOverride {
  id: string;
  featureFlagId: string;
  tenantId: string;
  value: unknown;
  createdAt: string;
  updatedAt: string;
  tenant?: {
    id: string;
    slug: string;
    name: string;
  };
}

export interface CreateFeatureFlagInput {
  key: string;
  name: string;
  description?: string;
  type?: 'BOOLEAN' | 'STRING' | 'NUMBER' | 'JSON';
  defaultValue: unknown;
  category?: string;
}

export interface UpdateFeatureFlagInput {
  name?: string;
  description?: string;
  type?: 'BOOLEAN' | 'STRING' | 'NUMBER' | 'JSON';
  defaultValue?: unknown;
  status?: 'ACTIVE' | 'INACTIVE';
  category?: string;
}

// Recruiting Types
export interface ScheduleInterviewInput {
  candidateId: string;
  interviewTypeId: string;
  scheduledAt: string;
  duration: number;
  interviewers: Array<{
    employeeId: string;
    role?: string;
    isPrimary?: boolean;
  }>;
  meetingLink?: string;
  location?: string;
  notes?: string;
  timezone?: string;
  sendInvite?: boolean;
}

export interface InterviewFeedbackInput {
  recommendation?: 'STRONG_YES' | 'YES' | 'NEUTRAL' | 'NO' | 'STRONG_NO';
  overallRating?: number;
  criteriaScores?: Array<{
    criterionId: string;
    score: number;
    notes?: string;
  }>;
  strengths?: string;
  concerns?: string;
  additionalNotes?: string;
  isAnonymous?: boolean;
  isDraft?: boolean;
}

export interface Interview {
  id: string;
  candidateId: string;
  interviewTypeId: string;
  interviewType?: InterviewType;
  candidate?: Candidate;
  scheduledAt: string;
  duration: number;
  status: 'SCHEDULED' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
  meetingLink?: string;
  location?: string;
  notes?: string;
  interviewers: InterviewerAssignment[];
  feedback?: InterviewFeedback[];
  createdAt: string;
  updatedAt: string;
}

export interface InterviewerAssignment {
  id: string;
  interviewId: string;
  employeeId: string;
  employee?: {
    id: string;
    legalFirstName: string;
    legalLastName: string;
    preferredName?: string;
    workEmail: string;
    jobTitle?: string;
  };
  role?: string;
  isPrimary: boolean;
  confirmed: boolean;
  confirmedAt?: string;
}

export interface InterviewType {
  id: string;
  name: string;
  category: 'SCREENING' | 'TECHNICAL' | 'BEHAVIORAL' | 'CULTURE' | 'FINAL' | 'OTHER';
  description?: string;
  defaultDuration: number;
  isActive: boolean;
  scorecardTemplateId?: string;
  scorecardTemplate?: ScorecardTemplate;
}

export interface ScorecardTemplate {
  id: string;
  name: string;
  description?: string;
  criteria: ScorecardCriterion[];
  isActive: boolean;
}

export interface ScorecardCriterion {
  id: string;
  name: string;
  description?: string;
  weight: number;
  order: number;
}

export interface InterviewFeedback {
  id: string;
  interviewId: string;
  interviewerId: string;
  interviewer?: {
    id: string;
    legalFirstName: string;
    legalLastName: string;
    preferredName?: string;
  };
  recommendation?: 'STRONG_YES' | 'YES' | 'NEUTRAL' | 'NO' | 'STRONG_NO';
  overallRating?: number;
  criteriaScores?: Array<{
    criterionId: string;
    score: number;
    notes?: string;
  }>;
  strengths?: string;
  concerns?: string;
  additionalNotes?: string;
  isAnonymous: boolean;
  isDraft: boolean;
  submittedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Candidate {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  resumeUrl?: string;
  source?: string;
  stage: string;
  jobPostingId?: string;
  jobPosting?: JobPosting;
  createdAt: string;
  updatedAt: string;
}

export interface JobPosting {
  id: string;
  title: string;
  departmentId?: string;
  department?: {
    id: string;
    name: string;
  };
  locationId?: string;
  location?: {
    id: string;
    name: string;
  };
  status: 'DRAFT' | 'PUBLISHED' | 'CLOSED';
  description?: string;
  requirements?: string;
  createdAt: string;
  updatedAt: string;
}

// Company Documents types
export type CompanyDocumentCategory =
  | 'COMPANY_POLICY'
  | 'EMPLOYEE_HANDBOOK'
  | 'CODE_OF_CONDUCT'
  | 'SAFETY_PROCEDURES'
  | 'IT_SECURITY'
  | 'BENEFITS_INFO'
  | 'COMPLIANCE'
  | 'TRAINING_MATERIAL'
  | 'OTHER';

export type AcknowledgmentSignatureType = 'CHECKBOX' | 'TYPED_NAME' | 'DRAWN';

export interface CompanyDocumentVersion {
  id: string;
  documentId: string;
  versionNumber: number;
  s3Key: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  changelog?: string;
  isActive: boolean;
  requiresReAcknowledgment: boolean;
  effectiveDate?: string;
  uploadedBy: string;
  uploadedAt: string;
}

export interface DocumentAcknowledgment {
  id: string;
  documentVersionId: string;
  employeeId: string;
  employeeName?: string;
  employeeEmail?: string;
  employee?: {
    id: string;
    legalFirstName: string;
    legalLastName: string;
    preferredName?: string;
    workEmail: string;
  };
  acknowledgedAt: string;
  ipAddress?: string;
  userAgent?: string;
  signatureType: AcknowledgmentSignatureType;
  signatureData?: {
    typedName?: string;
    drawingData?: string;
  };
  acknowledgmentHash?: string;
}

export interface CompanyDocument {
  id: string;
  title: string;
  description?: string;
  category: CompanyDocumentCategory;
  isActive: boolean;
  requiresAcknowledgment: boolean;
  acknowledgmentDueDate?: string | null;
  currentVersionId?: string;
  currentVersion?: CompanyDocumentVersion;
  versions?: CompanyDocumentVersion[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  // Computed fields from API
  acknowledged?: boolean;
  acknowledgmentDate?: string;
  isOverdue?: boolean;
}

export interface CompanyDocumentListResponse {
  data: CompanyDocument[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CompanyDocumentStats {
  totalDocuments: number;
  activeDocuments: number;
  documentsRequiringAcknowledgment: number;
  averageAcknowledgmentRate: number;
  categoryCounts: Record<CompanyDocumentCategory, number>;
}

export interface AcknowledgmentStatusResponse {
  document: CompanyDocument;
  version: CompanyDocumentVersion;
  totalEmployees: number;
  acknowledgedCount: number;
  pendingCount: number;
  acknowledgmentRate: number;
  acknowledgments: DocumentAcknowledgment[];
  page: number;
  pageSize: number;
  totalPages: number;
}

// Learning - Course types
export type CourseStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type CourseFormat = 'ONLINE' | 'IN_PERSON' | 'HYBRID' | 'SELF_PACED';
export type LessonType = 'VIDEO' | 'DOCUMENT' | 'QUIZ' | 'ASSIGNMENT' | 'INTERACTIVE' | 'TEXT';

export interface Course {
  id: string;
  code: string;
  title: string;
  description?: string;
  category?: string;
  duration?: number;
  format?: CourseFormat;
  instructorId?: string;
  instructor?: {
    id: string;
    legalFirstName: string;
    legalLastName: string;
    preferredName?: string;
  };
  maxParticipants?: number;
  status: CourseStatus;
  thumbnailUrl?: string;
  prerequisites?: string[];
  learningObjectives?: string[];
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  lessonCount?: number;
  enrollmentCount?: number;
}

export interface Lesson {
  id: string;
  courseId: string;
  title: string;
  description?: string;
  type: LessonType;
  contentUrl?: string;
  content?: string;
  duration?: number;
  orderIndex: number;
  isMandatory: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCourseInput {
  code: string;
  title: string;
  description?: string;
  category?: string;
  duration?: number;
  format?: CourseFormat;
  instructorId?: string;
  maxParticipants?: number;
  status?: CourseStatus;
  thumbnailUrl?: string;
  prerequisites?: string[];
  learningObjectives?: string[];
}

export interface UpdateCourseInput {
  code?: string;
  title?: string;
  description?: string;
  category?: string;
  duration?: number;
  format?: CourseFormat;
  instructorId?: string;
  maxParticipants?: number;
  status?: CourseStatus;
  thumbnailUrl?: string;
  prerequisites?: string[];
  learningObjectives?: string[];
}

export interface CreateLessonInput {
  courseId: string;
  title: string;
  description?: string;
  type: LessonType;
  contentUrl?: string;
  content?: string;
  duration?: number;
  orderIndex: number;
  isMandatory?: boolean;
}

export interface UpdateLessonInput {
  title?: string;
  description?: string;
  type?: LessonType;
  contentUrl?: string;
  content?: string;
  duration?: number;
  orderIndex?: number;
  isMandatory?: boolean;
}
